import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from './firebase'
export interface Category {
  id?: string
  name: string
  slug: string
  description?: string
  createdAt: any
  updatedAt: any
}
// Generate a slug from name
export const generateCategorySlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^\w\s]/gi, '')
    .replace(/\s+/g, '-')
}
// Get all categories
export const getAllCategories = async () => {
  const categoriesRef = collection(db, 'categories')
  const q = query(categoriesRef, orderBy('name', 'asc'))
  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Category[]
}
// Get category by ID
export const getCategoryById = async (id: string) => {
  const docRef = doc(db, 'categories', id)
  const docSnap = await getDoc(docRef)
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as Category
  }
  return null
}
// Get category by slug
export const getCategoryBySlug = async (slug: string) => {
  const categoriesRef = collection(db, 'categories')
  const q = query(categoriesRef, where('slug', '==', slug))
  const snapshot = await getDocs(q)
  if (!snapshot.empty) {
    const doc = snapshot.docs[0]
    return { id: doc.id, ...doc.data() } as Category
  }
  return null
}
// Create a new category
export const createCategory = async (categoryData: Partial<Category>) => {
  const slug = generateCategorySlug(categoryData.name || 'uncategorized')
  const newCategory = {
    name: categoryData.name || 'Uncategorized',
    slug,
    description: categoryData.description || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
  const docRef = await addDoc(collection(db, 'categories'), newCategory)
  return { id: docRef.id, ...newCategory }
}
// Update a category
export const updateCategory = async (
  id: string,
  categoryData: Partial<Category>,
) => {
  const categoryRef = doc(db, 'categories', id)
  // If name is changed, update slug
  if (categoryData.name) {
    categoryData.slug = generateCategorySlug(categoryData.name)
  }
  // Add updated timestamp
  const updateData = {
    ...categoryData,
    updatedAt: serverTimestamp(),
  }
  await updateDoc(categoryRef, updateData)
  return { id, ...categoryData }
}
// Delete a category
export const deleteCategory = async (id: string) => {
  await deleteDoc(doc(db, 'categories', id))
  return id
}
// Get articles by category
export const getArticlesByCategory = async (categoryId: string) => {
  const articlesRef = collection(db, 'articles')
  const q = query(
    articlesRef,
    where('categoryId', '==', categoryId),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }))
}
